import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Menu,
  LucideIcon,
} from 'lucide-react';

interface BottomNavItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;
  permission: string;
}

// Core navigation items for bottom nav (most used)
const bottomNavItems: BottomNavItem[] = [
  { path: '/', labelKey: 'navigation:menu.dashboard', icon: LayoutDashboard, permission: 'dashboard_view' },
  { path: '/inventory', labelKey: 'navigation:menu.inventory', icon: Package, permission: 'inventory_view' },
  { path: '/sales', labelKey: 'navigation:menu.sales', icon: ShoppingCart, permission: 'sales_view' },
  { path: '/customers', labelKey: 'navigation:menu.customers', icon: Users, permission: 'customers_view' },
];

interface BottomNavProps {
  onMenuClick: () => void;
}

export default function BottomNav({ onMenuClick }: BottomNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission } = useAuth();

  // Filter items based on permissions
  const visibleItems = bottomNavItems.filter(item => hasPermission(item.permission));

  // Only show first 4 items + menu
  const displayItems = visibleItems.slice(0, 4);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-dark-card border-t border-light-border dark:border-dark-border sm:hidden safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {displayItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full px-1 transition-colors ${
                isActive
                  ? 'text-accent-600 dark:text-accent-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />
              <span className="text-[10px] mt-1 font-medium truncate max-w-full">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}

        {/* More menu button */}
        <button
          onClick={onMenuClick}
          className="flex flex-col items-center justify-center flex-1 h-full px-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <Menu size={22} />
          <span className="text-[10px] mt-1 font-medium">
            {t('navigation:menu.more', { defaultValue: 'More' })}
          </span>
        </button>
      </div>
    </nav>
  );
}
